import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Scroll } from "@phosphor-icons/react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [rolls, setRolls] = useState([]);
  const [mfr, setMfr] = useState(null);
  const [series, setSeries] = useState(null);
  const { isAdmin, isAuthed, canSeePricing, maskMoney } = useAuth();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/products/${id}`);
      setProduct(data);
      const [{ data: r }, { data: ms }, { data: ss }] = await Promise.all([
        api.get("/rolls", { params: { product_id: id } }),
        api.get("/manufacturers"),
        api.get("/series"),
      ]);
      setRolls(r);
      setMfr(ms.find((m) => m.id === data.manufacturer_id));
      setSeries(ss.find((s) => s.id === data.series_id));
    })();
  }, [id]);

  if (!product) return <div className="p-8 text-slate-500">Loading…</div>;

  const totalMeters = rolls.reduce((s, r) => s + (r.current_length_m || 0), 0);

  return (
    <div className="p-8 max-w-[1400px]">
      <Link to="/browse" className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.15em] uppercase text-slate-500 hover:text-slate-900 mb-6" data-testid="back-browse">
        <ArrowLeft size={14} /> Back to catalogue
      </Link>

      <div className="grid lg:grid-cols-[auto_1fr] gap-8 mb-10 items-start">
        <div className="w-40 h-40 border border-slate-200 rounded-md" style={{ background: product.color_hex || "#e2e8f0" }} />
        <div>
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">
            {mfr?.name} · {series?.name}
          </div>
          <h1 className="font-heading text-5xl font-bold tracking-tight text-slate-900">{product.name}</h1>
          <div className="font-mono text-sm text-slate-600 mt-2">SKU base: {product.base_sku}</div>
          {product.notes && <p className="text-sm text-slate-700 mt-4 max-w-2xl">{product.notes}</p>}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 mt-8 border border-slate-200">
            <div className="p-5 border-r border-slate-200">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Available</div>
              <div className="font-mono text-2xl font-semibold text-slate-900 mt-1">{totalMeters.toFixed(2)}m</div>
            </div>
            <div className="p-5 border-r border-slate-200">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Rolls</div>
              <div className="font-mono text-2xl font-semibold text-slate-900 mt-1">{rolls.length}</div>
            </div>
            {canSeePricing && product.cost_per_meter != null && (
              <div className="p-5 border-r border-slate-200">
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Cost / m</div>
                <div className="font-mono text-2xl font-semibold text-slate-900 mt-1">{maskMoney(`$${product.cost_per_meter.toFixed(2)}`)}</div>
              </div>
            )}
            {canSeePricing && product.price_per_meter != null && (
              <div className="p-5">
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Price / m</div>
                <div className="font-mono text-2xl font-semibold text-blue-600 mt-1">{maskMoney(`$${product.price_per_meter.toFixed(2)}`)}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">Rolls in stock</h2>
      {rolls.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-12 text-center">
          <Scroll size={28} className="mx-auto text-slate-300 mb-2" />
          <div className="text-sm text-slate-500">No rolls available for this product.</div>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                <th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3">Batch</th>
                <th className="px-4 py-3">Mfg Date</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3 text-right">Initial</th>
                <th className="px-4 py-3 text-right">Remaining</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rolls.map((r) => {
                const RowTag = isAuthed ? Link : "tr";
                const props = isAuthed ? { to: `/rolls/${r.id}` } : {};
                return (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs">{r.sku}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.batch_number}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.manufacturing_date}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.expiry_date}</td>
                    <td className="px-4 py-3 font-mono text-right tabular-nums">{r.initial_length_m.toFixed(2)}m</td>
                    <td className="px-4 py-3 font-mono text-right tabular-nums font-semibold">{r.current_length_m.toFixed(2)}m</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm ${
                        r.status === "active" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-600 border border-slate-200"
                      }`}>{r.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
